import { useState } from "react";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@nosible/shared/components/ui/dialog";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import { Plus, Building2 } from "lucide-react";
import { trpc } from "@/admin/lib/trpc";

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .substring(0, 50);
}

type AdminCreateOrgProps = {
  onSuccess?: () => void;
  triggerClassName?: string;
  triggerLabel?: string;
  showTriggerIcon?: boolean;
};

export function AdminCreateOrg({
  onSuccess,
  triggerClassName,
  triggerLabel = "Create Organization",
  showTriggerIcon = true,
}: AdminCreateOrgProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [url, setUrl] = useState("");

  const utils = trpc.useUtils();

  const createOrgMutation = trpc.admin.createOrganization.useMutation({
    onSuccess: () => {
      setIsOpen(false);
      setName("");
      setSlug("");
      setUrl("");
      utils.admin.listOrganizations.invalidate();
      onSuccess?.();
    },
  });

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slug || slug === generateSlug(name)) {
      setSlug(generateSlug(value));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;

    createOrgMutation.mutate({
      name: name.trim(),
      slug: slug.trim(),
      url: url.trim() || undefined,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className={triggerClassName} variant="outline" size="sm">
          {showTriggerIcon && <Plus className="h-4 w-4 mr-2" />}
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-violet-pulse" />
            Create Organization
          </DialogTitle>
          <DialogDescription>
            Create a new organization. The organization will be created without
            any members.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="org-name">
              Organization Name <span className="text-red-400">*</span>
            </Label>
            <Input
              id="org-name"
              placeholder="Acme Inc."
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              disabled={createOrgMutation.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="org-slug">
              Slug <span className="text-red-400">*</span>
            </Label>
            <Input
              id="org-slug"
              placeholder="acme-inc"
              value={slug}
              onChange={(e) =>
                setSlug(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, "")
                    .substring(0, 50)
                )
              }
              disabled={createOrgMutation.isPending}
            />
            <p className="text-xs text-chromium-mist">
              Used in URLs: /organizations/{slug || "your-slug"}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="org-url">Website URL</Label>
            <Input
              id="org-url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={createOrgMutation.isPending}
            />
            <p className="text-xs text-chromium-mist">
              Optional website or domain associated with this organization
            </p>
          </div>

          {createOrgMutation.error && (
            <p className="text-sm text-red-400">
              Error: {createOrgMutation.error.message}
            </p>
          )}

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={createOrgMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                createOrgMutation.isPending || !name.trim() || !slug.trim()
              }
            >
              {createOrgMutation.isPending
                ? "Creating..."
                : "Create Organization"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
